import React, { FC, ReactNode, createContext, useCallback, useMemo } from 'react';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { TimezoneContextValue, ZoneChoice, ZoneOption } from 'src/features/timezone/types/timezone.types';
import { UTC_ZONE, localZoneId, resolveZone } from 'src/features/timezone/utils/datetime';

const ZONE_CHOICES = ['store', 'utc', 'local'] as const;

export const TimezoneContext = createContext<TimezoneContextValue | undefined>(undefined);

interface TimezoneProviderProps {
  storeZone?: string;
  children: ReactNode;
}

export const TimezoneProvider: FC<TimezoneProviderProps> = ({ storeZone, children }) => {
  const defaultChoice: ZoneChoice = storeZone ? 'store' : 'local';
  const [rawChoice, setRawChoice] = useQueryState('tz', parseAsStringLiteral(ZONE_CHOICES));
  const requested = rawChoice ?? defaultChoice;
  const choice = requested === 'store' && !storeZone ? defaultChoice : requested;

  const setChoice = useCallback((next: ZoneChoice) => {
    setRawChoice(next === defaultChoice ? null : next);
  }, [setRawChoice, defaultChoice]);

  const value = useMemo<TimezoneContextValue>(() => {
    const options: ZoneOption[] = [
      ...(storeZone ? [{ choice: 'store' as const, zoneId: storeZone, role: 'Store' }] : []),
      { choice: 'utc' as const, zoneId: UTC_ZONE, role: 'UTC' },
      { choice: 'local' as const, zoneId: localZoneId(), role: 'Your computer' }
    ];
    return { choice, zone: resolveZone(choice, storeZone), options, setChoice };
  }, [choice, storeZone, setChoice]);

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
};
